import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  addMomentMins,
  utcToTz,
  dateToMoment,
} from '@mummssoftware/utils/dates';
import moment from 'moment-timezone';
import { formatPimDate } from '@mummssoftware/utils/formatters';
import { hhmmToMin } from '@mummssoftware/utils/timeUtils';
import { hasTimerDuration } from '@mummssoftware/utils/utils';
import { GlobalContext } from '@mummssoftware/global-context/GlobalContext';
import { getDurationMinutesFromVisitSources } from '@mummssoftware/utils/visitUtils';
import EditStartTime from './EditStartTime';
import { EditVisitDiv } from '../../components';
import { Context } from '../../context';
import { VisitContext } from '../Context/contexts/VisitContext';
import { SET_SAVED_VISIT } from '../Context/constants';

type EditTimeProps = {
  /** Label and picker as two grid cells (record visit intro grid). */
  inline?: boolean;
};

export const EditTime = ({ inline = false }: EditTimeProps) => {
  const { appVisit } = useContext(GlobalContext);
  const { savedVisit, timerDuration, dispatchVisit } = useContext(VisitContext);
  const { currentUser } = useContext(Context);

  const timezone = currentUser.siteTimezone || moment.tz.guess();
  const savedStartTime = savedVisit?.startDate
    ? utcToTz(savedVisit?.startDate, timezone)
    : new Date();
  const [startTime, setStartTime] = useState(savedStartTime);

  /**
   * When the visit start is updated elsewhere (record-visit intro row saves to PIM, or any
   * `SET_SAVED_VISIT` with a new `startDate`), keep the timer UI on the same instant.
   */
  useEffect(() => {
    if (savedVisit?.startDate) {
      setStartTime(utcToTz(savedVisit.startDate, timezone));
    }
  }, [savedVisit, timezone]);

  const durationMinsFromVisit = useMemo(
    () => getDurationMinutesFromVisitSources(savedVisit, appVisit) ?? 0,
    [savedVisit, appVisit],
  );

  const durationMins = hasTimerDuration(timerDuration)
    ? hhmmToMin(timerDuration[0], timerDuration[1])
    : durationMinsFromVisit;
  const addDuration = addMomentMins(durationMins);

  const updateSavedVisit = (newStartTime: Date) => {
    const start = dateToMoment(newStartTime); // dateToMoment splices out timezone
    const startDate = formatPimDate(start);
    const endDate = formatPimDate(addDuration(start));

    const updatedSavedVisit = {
      ...savedVisit,
      startDate, //  moment.tz
      endDate,
    };

    dispatchVisit({
      type: SET_SAVED_VISIT,
      payload: updatedSavedVisit,
    });
  };

  const onStartTimeChange = (newTime: Date) => {
    setStartTime(newTime);
    updateSavedVisit(newTime);
  };

  if (inline) {
    return (
      <EditStartTime
        inline
        startTime={startTime}
        onChange={onStartTimeChange}
      />
    );
  }
  return (
    <EditVisitDiv id="dashboard-edit-time">
      <EditStartTime startTime={startTime} onChange={onStartTimeChange} />
    </EditVisitDiv>
  );
};
export default EditTime;
